import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  ChevronRight,
  CheckCircle2,
  Calendar,
  Send,
  ArrowLeft,
  LayoutTemplate,
  Eye,
} from 'lucide-react';
import { toast } from '../../ui';
import { useMailTemplates } from '../../hooks/useMailTemplates';
import { mailCampaignsService } from '../../services/mail-campaigns.service';
import { useProduct } from '../../contexts/ProductContext';
import { RecipientSelector } from '../mail/RecipientSelector';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmailPreviewModal } from '../mail/EmailPreview';

// ============================================
// Step Indicator
// ============================================

const STEPS = [
  { label: 'Template', icon: LayoutTemplate },
  { label: 'Destinatários', icon: Mail },
  { label: 'Envio', icon: Send },
];

function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center gap-0">
      {STEPS.map((step, index) => {
        const stepNumber = index + 1;
        const isCompleted = stepNumber < currentStep;
        const isActive = stepNumber === currentStep;
        const Icon = step.icon;

        return (
          <div key={stepNumber} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                  isCompleted
                    ? 'bg-ink-primary border-ink-primary text-canvas'
                    : isActive
                      ? 'bg-surface border-ink-primary text-ink-primary'
                      : 'bg-surface border-border-strong text-ink-tertiary'
                }`}
              >
                {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
              </div>
              <span
                className={`text-xs font-medium ${
                  isActive
                    ? 'text-ink-primary'
                    : isCompleted
                      ? 'text-ink-secondary'
                      : 'text-ink-tertiary'
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`w-16 h-0.5 mb-5 mx-1 transition-colors ${
                  stepNumber < currentStep ? 'bg-ink-primary' : 'bg-subtle'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Campaign Wizard
// ============================================

interface CampaignWizardProps {
  editId: number | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export function CampaignWizard({ editId, onClose, onCompleted }: CampaignWizardProps) {
  const queryClient = useQueryClient();

  const { selectedProductId } = useProduct();

  // Wizard state
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [previewTemplateId, setPreviewTemplateId] = useState<number | null>(null);
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [campaignId, setCampaignId] = useState<number | null>(editId ?? null);

  const { data: templatesData, isLoading: templatesLoading } = useMailTemplates(
    selectedProductId ?? 0
  );

  // Load existing campaign when editing a draft
  const { data: existingCampaign } = useQuery({
    queryKey: ['mail-campaign', editId],
    queryFn: () => mailCampaignsService.getById(editId!),
    enabled: !!editId,
    staleTime: 0,
  });

  // Hydrate wizard state once when campaign data arrives
  useEffect(() => {
    if (!existingCampaign) {
      return;
    }
    const c: any = existingCampaign;
    setName(c.name ?? '');
    setSelectedTemplateId(c.templateId ?? null);
    const recipients: any[] = Array.isArray(c.recipients) ? c.recipients : [];
    setSelectedClients(
      recipients
        .filter((r) => r.clientId !== null && r.clientId !== undefined)
        .map((r) => r.clientId as number)
    );
    setSelectedLeads(
      recipients
        .filter((r) => r.leadId !== null && r.leadId !== undefined)
        .map((r) => r.leadId as number)
    );
    if (c.scheduledAt) {
      const d = new Date(c.scheduledAt);
      const pad = (n: number) => String(n).padStart(2, '0');
      setScheduledAt(
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      );
    }
    if (c.id !== null && c.id !== undefined) {
      setCampaignId(c.id as number);
    }
  }, [existingCampaign]);

  const templates: any[] = Array.isArray(templatesData)
    ? templatesData
    : (templatesData as any)?.data || (templatesData as any)?.templates || [];

  // Mutation: create campaign
  const createCampaignMutation = useMutation({
    mutationFn: (data: { name: string; templateId: number; productId: number }) =>
      mailCampaignsService.create(data),
    onSuccess: (data) => {
      setCampaignId(data.id ?? data.campaign?.id);
      queryClient.invalidateQueries({ queryKey: ['mail-campaigns'] });
      setStep(2);
    },
    onError: () => toast.error('Erro ao criar campanha'),
  });

  // Mutation: update an existing draft (wizard opened with editId, or user went back to step 1)
  const updateCampaignMutation = useMutation({
    mutationFn: ({ id, name, templateId }: { id: number; name: string; templateId: number }) =>
      mailCampaignsService.update(id, { name, templateId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-campaigns'] });
      setStep(2);
    },
    onError: () => toast.error('Erro ao atualizar campanha'),
  });

  // Mutation: add recipients
  const addRecipientsMutation = useMutation({
    mutationFn: ({ id, clients, leads }: { id: number; clients: number[]; leads: number[] }) =>
      mailCampaignsService.addRecipients(id, { clients, leads }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-campaigns'] });
      setStep(3);
    },
    onError: () => toast.error('Erro ao adicionar destinatários'),
  });

  // Mutation: send campaign
  const sendMutation = useMutation({
    mutationFn: ({ id, scheduledAt }: { id: number; scheduledAt?: string }) =>
      mailCampaignsService.send(id, scheduledAt || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-campaigns'] });
      toast.success(
        scheduledAt ? 'Campanha agendada com sucesso!' : 'Campanha enviada com sucesso!'
      );
      onCompleted?.();
      onClose();
    },
    onError: () => toast.error('Erro ao enviar campanha'),
  });

  // ---- Step handlers ----

  const handleStep1Next = () => {
    if (!name.trim()) {
      toast.error('Informe o nome da campanha');
      return;
    }
    if (!selectedTemplateId) {
      toast.error('Selecione um template');
      return;
    }
    if (!selectedProductId) {
      return;
    }

    // Já existe uma campanha (rascunho aberto para edição): atualiza, nunca cria outra.
    if (campaignId) {
      updateCampaignMutation.mutate({
        id: campaignId,
        name: name.trim(),
        templateId: selectedTemplateId,
      });
      return;
    }

    createCampaignMutation.mutate({
      name: name.trim(),
      templateId: selectedTemplateId,
      productId: selectedProductId,
    });
  };

  const handleStep2Next = () => {
    if (!campaignId) {
      return;
    }
    if (selectedClients.length === 0 && selectedLeads.length === 0) {
      toast.error('Selecione ao menos um destinatário');
      return;
    }

    addRecipientsMutation.mutate({
      id: campaignId,
      clients: selectedClients,
      leads: selectedLeads,
    });
  };

  const handleSend = (schedule?: boolean) => {
    if (!campaignId) {
      return;
    }
    if (schedule && !scheduledAt) {
      toast.error('Informe a data/hora para agendamento');
      return;
    }

    sendMutation.mutate({
      id: campaignId,
      scheduledAt: schedule ? scheduledAt : undefined,
    });
  };

  if (!selectedProductId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-ink-tertiary">
        <Send className="w-12 h-12" />
        <p className="text-base font-medium">Selecione um produto para criar campanhas</p>
      </div>
    );
  }

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const totalRecipients = selectedClients.length + selectedLeads.length;

  return (
    <div className="space-y-8">
      {/* Step indicator */}
      <div className="flex justify-center">
        <StepIndicator currentStep={step} />
      </div>

      {/* Step 1: Template */}
      {step === 1 && (
        <div className="bg-surface rounded-[14px] border border-border p-6 space-y-6">
          <h2 className="text-lg font-semibold text-ink-primary">Nome e Template</h2>

          {/* Campaign name */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-ink-secondary">
              Nome da campanha <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Promoção de Março"
              className="input w-full"
            />
          </div>

          {/* Template grid */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-ink-secondary">
              Template <span className="text-danger">*</span>
            </label>
            {templatesLoading ? (
              <div className="flex items-center justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-ink-tertiary border border-dashed border-border-strong rounded-xl">
                <LayoutTemplate className="w-8 h-8" />
                <p className="text-sm">Nenhum template disponível</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templates.map((template) => {
                  const tid = template.id as number;
                  const isSelected = selectedTemplateId === tid;

                  return (
                    <div
                      key={tid}
                      className={`relative text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        isSelected
                          ? 'border-ink-primary bg-subtle'
                          : 'border-border hover:border-border-strong bg-surface hover:bg-subtle'
                      }`}
                      onClick={() => setSelectedTemplateId(tid)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-subtle">
                          <Mail
                            className={`w-4 h-4 ${isSelected ? 'text-ink-primary' : 'text-ink-tertiary'}`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate text-ink-primary">
                            {(template.name as string) || '—'}
                          </p>
                          {template.subject && (
                            <p className="text-xs text-ink-tertiary truncate mt-0.5">
                              {template.subject as string}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewTemplateId(tid);
                          }}
                          className="p-1.5 rounded-md text-ink-tertiary hover:text-ink-primary hover:bg-surface transition-colors flex-shrink-0"
                          title="Pré-visualizar e-mail"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-ink-primary flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border text-ink-secondary bg-surface rounded-lg hover:bg-subtle transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </button>
            <button
              onClick={handleStep1Next}
              disabled={createCampaignMutation.isPending || updateCampaignMutation.isPending}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-ink-primary text-canvas rounded-lg hover:bg-ink-secondary transition-colors disabled:opacity-50"
            >
              {createCampaignMutation.isPending
                ? 'Criando...'
                : updateCampaignMutation.isPending
                  ? 'Salvando...'
                  : 'Próximo'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Recipients */}
      {step === 2 && (
        <div className="bg-surface rounded-[14px] border border-border p-6 space-y-6">
          <h2 className="text-lg font-semibold text-ink-primary">Destinatários</h2>

          <RecipientSelector
            productId={selectedProductId}
            selectedClients={selectedClients}
            selectedLeads={selectedLeads}
            onClientsChange={setSelectedClients}
            onLeadsChange={setSelectedLeads}
          />

          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border text-ink-secondary bg-surface rounded-lg hover:bg-subtle transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </button>
            <button
              onClick={handleStep2Next}
              disabled={addRecipientsMutation.isPending}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-ink-primary text-canvas rounded-lg hover:bg-ink-secondary transition-colors disabled:opacity-50"
            >
              {addRecipientsMutation.isPending ? 'Salvando...' : 'Próximo'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Send */}
      {step === 3 && (
        <div className="bg-surface rounded-[14px] border border-border p-6 space-y-6">
          <h2 className="text-lg font-semibold text-ink-primary">Confirmar e Enviar</h2>

          {/* Summary */}
          <div className="bg-canvas rounded-xl border border-border divide-y divide-border">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-ink-secondary">Campanha</span>
              <span className="text-sm font-medium text-ink-primary">{name}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-ink-secondary">Template</span>
              <span className="text-sm font-medium text-ink-primary">
                {(selectedTemplate?.name as string) ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-ink-secondary">Destinatários</span>
              <span className="text-sm font-medium text-ink-primary">
                {totalRecipients} pessoa{totalRecipients !== 1 ? 's' : ''}
                {selectedClients.length > 0 &&
                  ` (${selectedClients.length} cliente${selectedClients.length !== 1 ? 's' : ''})`}
                {selectedLeads.length > 0 &&
                  ` (${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''})`}
              </span>
            </div>
          </div>

          {/* Scheduling */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-ink-secondary">
              <Calendar className="w-4 h-4" />
              Agendar envio — data e hora (opcional)
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="input w-full"
              min={new Date().toISOString().slice(0, 16)}
              step={60}
            />
            <p className="text-xs text-ink-tertiary">
              Clique em cada parte do campo (mês, dia, ano, hora, minuto) para editar. Deixe em
              branco para enviar imediatamente.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border text-ink-secondary bg-surface rounded-lg hover:bg-subtle transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </button>
            <div className="flex items-center gap-3">
              {scheduledAt && (
                <button
                  onClick={() => handleSend(true)}
                  disabled={sendMutation.isPending}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border border-ink-primary text-ink-primary bg-surface rounded-lg hover:bg-subtle transition-colors disabled:opacity-50"
                >
                  <Calendar className="w-4 h-4" />
                  {sendMutation.isPending ? 'Agendando...' : 'Agendar'}
                </button>
              )}
              <button
                onClick={() => handleSend(false)}
                disabled={sendMutation.isPending}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-ink-primary text-canvas rounded-lg hover:bg-ink-secondary transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {sendMutation.isPending ? 'Enviando...' : 'Enviar Agora'}
              </button>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const previewTemplate = templates.find((t) => t.id === previewTemplateId);
        const previewBlocks: any[] = Array.isArray(previewTemplate?.blocks)
          ? (previewTemplate?.blocks as any[])
          : [];
        return (
          <EmailPreviewModal
            blocks={previewBlocks}
            open={previewTemplateId !== null}
            onClose={() => setPreviewTemplateId(null)}
          />
        );
      })()}
    </div>
  );
}

export default CampaignWizard;
